import { useMutation, useQuery } from "@apollo/client";
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { GET_SALON_WHATSAPP, UPDATE_SALON_WHATSAPP } from "@/graphql/salon";
import { useSalonStore } from "@/lib/store/salon";

/**
 * Reads/updates the salon's client-facing WhatsApp number. The backend normalizes
 * to E.164 digits on write, so the value that comes back may differ from what the
 * owner typed — we re-seed from the server response.
 */
export function useWhatsappNumber() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id ?? "";

  const [value, setValue] = useState("");
  const [saving, setSaving] = useState(false);
  const seeded = useRef(false);

  const { data, loading } = useQuery(GET_SALON_WHATSAPP, {
    variables: { salonId },
    skip: !salonId,
    fetchPolicy: "cache-and-network",
  });

  const [updateSalon] = useMutation(UPDATE_SALON_WHATSAPP);

  useEffect(() => {
    if (!seeded.current && data?.getSalon) {
      setValue(data.getSalon.whatsappNumber ?? "");
      seeded.current = true;
    }
  }, [data]);

  const save = useCallback(async () => {
    if (!salonId) return;
    setSaving(true);
    try {
      const result = await updateSalon({
        variables: { salonId, input: { whatsappNumber: value } },
      });
      // Re-seed with the server-normalized value.
      setValue(result.data?.updateSalon.whatsappNumber ?? "");
      toast.success("WhatsApp number saved");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save WhatsApp number");
    } finally {
      setSaving(false);
    }
  }, [salonId, value, updateSalon]);

  return { value, setValue, loading, saving, save };
}
